import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './App.css';

const API_URL = '';

function App() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [status, setStatus] = useState('idle');
  const chatEnd = useRef(null);

  useEffect(() => {
    chatEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim()) return;
    
    const userMsg = { role: 'user', content: input };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    setStatus('thinking...');

    try {
      const res = await axios.post(`${API_URL}/chat`, {
        message: input,
        email: credentials.email,
        password: credentials.password
      });
      
      let data = res.data.response || res.data;
      // Unwrap nested response objects
      while (data && typeof data === 'object' && data.response && typeof data.response === 'object') {
        data = data.response;
      }
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: typeof data === 'object' ? data : { response: String(data) },
        status: res.data.status 
      }]);
      setStatus(res.data.status || 'done');
    } catch (err) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: { error: err.response?.data?.detail || err.message }
      }]);
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const renderContent = (content) => {
    if (!content) return null;
    if (typeof content === 'string') return <p>{String(content)}</p>;
    if (content.error) return <p className="error">❌ {String(content.error)}</p>;
    
    // Projects list
    if (content.projects) {
      return (
        <div>
          <h4>📁 Projects ({content.projects.length})</h4>
          {content.projects.map(p => (
            <div key={p.id} className="card">
              <strong>{p.name}</strong> (ID: {p.id})<br/>
              📊 {p.analyses_count || 0} analyses | 📂 {p.datasets_count || 0} datasets
            </div>
          ))}
        </div>
      );
    }
    
    // Datasets list
    if (content.datasets) {
      const list = Array.isArray(content.datasets) ? content.datasets : [];
      return (
        <div>
          <h4>📊 Datasets ({list.length})</h4>
          {list.map(d => (
            <div key={d.id} className="card">
              <strong>{d.original_filename || d.name}</strong> (ID: {d.id})<br/>
              📏 {d.row_count?.toLocaleString() || '?'} rows | 💾 {d.file_size ? (d.file_size/1e6).toFixed(1)+'MB' : '?'}
            </div>
          ))}
        </div>
      );
    }
    
    // Review
    if (content.review) {
      const r = content.review;
      return (
        <div>
          <h4>📋 Review (Grade: {r.grade || '?'})</h4>
          <p>{r.summary || ''}</p>
          {r.strengths && <p>✅ {r.strengths.join(', ')}</p>}
          {r.weaknesses && <p>⚠️ {r.weaknesses.join(', ')}</p>}
        </div>
      );
    }
    
    // Generic response
    if (content.response) {
      if (typeof content.response === 'object') {
        return <pre>{JSON.stringify(content.response, null, 2)}</pre>;
      }
      return <p>{String(content.response)}</p>;
    }
    
    return <pre>{JSON.stringify(content, null, 2)}</pre>;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = async () => {
    setMessages([]);
    try { await axios.post(`${API_URL}/reset`); } catch(e) {}
  };

  return (
    <div className="app">
      <div className="sidebar">
        <h2>🤖 OPTYMUM</h2>
        <div className={`status ${status}`}>{status}</div>
        <input type="email" placeholder="Email" value={credentials.email}
          onChange={e => setCredentials({...credentials, email: e.target.value})} />
        <input type="password" placeholder="Password" value={credentials.password}
          onChange={e => setCredentials({...credentials, password: e.target.value})} />
        <button onClick={clearChat}>🗑 Clear</button>
        <hr/>
        <button onClick={() => setInput('List my projects')}>📁 List Projects</button>
        <button onClick={() => setInput('List datasets')}>📊 List Datasets</button>
        <button onClick={() => setInput('Run CART analysis')}>🔬 Run CART</button>
      </div>
      
      <div className="chat">
        <div className="messages">
          {messages.length === 0 && (
            <div className="welcome">
              <h1>🤖 OPTYMUM Multi-Agent</h1>
              <p>AI-powered LGD/CCF analysis and validation</p>
              <p>Try: "List my projects" or "Run CART analysis"</p>
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i} className={`message ${msg.role}`}>
              <div className="role">{msg.role === 'user' ? '👤' : '🤖'}</div>
              <div className="bubble">{renderContent(msg.content)}</div>
              {msg.status && <span className="msg-status">{msg.status}</span>}
            </div>
          ))}
          {loading && <div className="message assistant"><div className="bubble">Thinking...</div></div>}
          <div ref={chatEnd} />
        </div>
        <div className="input-area">
          <textarea value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown} placeholder="Ask the agents..."
            rows={2} disabled={loading} />
          <button onClick={sendMessage} disabled={loading}>Send</button>
        </div>
      </div>
    </div>
  );
}

export default App;
